import {Box, CircularProgress, Grid, Typography} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// Components
import { Field, Formtitle, IperiaLayout } from '../../components'
// Hooks
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
// Utils
import { getDefaultRouteForStatus } from '../../utils/iperiaNavigation'
import { IperiaFormDefaultState, csp_choices, study_level_choices } from '../../utils/constants'
import { IperiaFormType } from '../../utils/sharedTypes'

const VerificationSummary = () => {
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, confirmSummary } = useIperiaStatus()
  const [submitting, setSubmitting] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [form, setForm] = useState<IperiaFormType>(IperiaFormDefaultState)

  useEffect(() => {
    if (!status?.learner_summary) return
    
    setForm({ 
      ...IperiaFormDefaultState, 
      data: { 
        ...IperiaFormDefaultState.data, 
        ...status.learner_summary 
      } 
    })
  }, [status?.learner_summary])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  const alreadyConfirmed = status.learner_summary_confirmed

  const handleConfirm = async () => {
    if (!globalId) return

    if (alreadyConfirmed) {
      navigate(`/iperia/${globalId}/nir`)
      return
    }

    setSubmitting(true)
    setConfirmError(null)
    try {
      const updated = await confirmSummary()
      navigate(`/iperia/${globalId}/${getDefaultRouteForStatus(updated)}`)
    } catch {
      setConfirmError('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    if (!globalId) return
    navigate(`/iperia/${globalId}/verification/formation`)
  }

  const getCspLabel = (value: string | undefined) => {
    if (!value) return '-'
    return csp_choices.find((c) => c.value === value)?.label || value
  }

  const getStudyLevelLabel = (value: string | undefined) => {
    if (!value) return '-'
    return study_level_choices.find((s) => s.value === value)?.label || value
  }

  const getAddressLabel = () => {
    return `${form.data.address}${form.data.address_line_2 ? ` (${form.data.address_line_2})` : ''}, ${form.data.postal_code} ${form.data.city}, ${form.data.country || 'France'}`
  }

  return (
    <IperiaLayout 
      productName={form.data.product_name}
      handleBack={handleBack}
      handleNext={handleConfirm}
      submitting={submitting}
      submittingNext={submitting}
      alreadyConfirmed={alreadyConfirmed}
    >
      <Formtitle
        title="Étape 4 : Récapitulatif"
        subtitle={
          alreadyConfirmed
            ? 'Vos informations ont déjà été confirmées. Vous pouvez continuer le parcours.'
            : 'Veuillez vérifier l\'ensemble de vos informations avant de confirmer.'
        }
      />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
          Informations personnelles
        </Typography>
        <Grid container spacing={1}>
          <Field label="Prénom" value={form.data.first_name} />
          <Field label="Nom d’usage" value={form.data.last_name} />
          {form.data.birth_name && <Field label="Nom de naissance" value={form.data.birth_name} />}
          <Field label="Date de naissance" value={form.data.birth_date} type="date" />
          <Field label="Email" value={form.data.email} />
          <Field label="Téléphone" value={form.data.mobile_phone} />
          <Field label="Niveau d'études" value={getStudyLevelLabel(form.data.study_level)} />
          <Field label="Catégorie socio-professionnelle" value={getCspLabel(form.data.csp)} />
          <Field label="Adresse" value={getAddressLabel()} />
        </Grid>

        <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
          Formation
        </Typography>
        <Grid container spacing={1}>
          <Field label="Formation" value={form.data.product_name || '-'} />
          <Field label="Date de début de session" value={form.data.session_start_date} type="date" />
        </Grid>
      </Box>

      {confirmError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {confirmError}
        </Typography>
      )}
    </IperiaLayout>
  )
}

export default VerificationSummary
